// The one piece of logic behind every Figma link field, in Figmafolio and in Aero.
// The brands differ only in how the notes and the guide look.
//
//   const link = useFigmaLinkRows({ rows: [{ id, url }], setUrl: (id, url) => ..., enabled })
//
//   link.noteKind(id, url)        which note to show under that field (or null)
//   link.handlePaste(id, event)   onPaste: a design link pasted over the whole field is converted
//   link.handleBlur(id, value)    onBlur: convert, and start flagging junk (never while typing)
//   link.recheck(id, url)         the "Check again" button
//   link.guide                    { openFor, open(id), close, recheckQuiet() } for the guide modal
//
// What it does, in order:
//   1. a design / file / branch / embed link becomes a prototype link (utils/figmaPaste.js);
//      a link that is already good is never rewritten
//   2. a prototype link is checked, once, 700 ms after it settles: on paste, when the page opens,
//      and when a link changes. Only the row that changed is asked about
//   3. the answer is 'viewable', 'not_viewable' or 'unknown' (shows nothing). Saving is never blocked
import { useCallback, useEffect, useRef, useState } from 'react';
import { classifyFigmaLink, convertPastedLink, protoFileKey } from './figmaPaste';
import { requestLinkCheck } from './figmaLinkCheckClient';

const SETTLE_MS = 700;

export default function useFigmaLinkRows({ rows, setUrl, enabled = true }) {
  // touched: pasted into or left, so a half-typed link is never scolded
  // converted: we changed this row's link into a prototype link (shows the quiet line)
  // checks: { [id]: { url, status } }, status checking | viewable | not_viewable | unknown
  const [touched, setTouched] = useState({});
  const [converted, setConverted] = useState({});
  const [checks, setChecks] = useState({});
  const [guideFor, setGuideFor] = useState(null);

  const latest = useRef({}); // the newest request per row, so an old answer never overwrites a new one
  const asked = useRef({}); // the link we last asked about, per row
  const sequence = useRef(0);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const setUrlRef = useRef(setUrl);
  setUrlRef.current = setUrl;

  const runCheck = useCallback((id, url, { silent = false } = {}) => {
    const key = protoFileKey(url);
    if (!key) return Promise.resolve('unknown');
    const mine = ++sequence.current;
    latest.current[id] = mine;
    asked.current[id] = url;
    if (!silent) setChecks((c) => ({ ...c, [id]: { url, status: 'checking' } }));
    return requestLinkCheck(key).then((status) => {
      if (latest.current[id] === mine) setChecks((c) => ({ ...c, [id]: { url, status } }));
      return status;
    });
  }, []);

  const handleBlur = (id, value) => {
    setTouched((t) => ({ ...t, [id]: true }));
    const result = convertPastedLink(value);
    if (result.changed) {
      setConverted((c) => ({ ...c, [id]: true }));
      setUrlRef.current(id, result.link);
    }
  };

  const handlePaste = (id, e) => {
    if (!enabled) return;
    const text = e.clipboardData && e.clipboardData.getData('text');
    if (!text) return;
    // Only take over when the paste replaces the whole field (empty, or all selected). Pasting into
    // the middle of existing text behaves as it always did; leaving the field converts.
    const el = e.target;
    const replacesAll = !el.value || (el.selectionStart === 0 && el.selectionEnd === el.value.length);
    const result = convertPastedLink(text);
    setTouched((t) => ({ ...t, [id]: true }));
    if (result.changed && replacesAll) {
      e.preventDefault();
      setConverted((c) => ({ ...c, [id]: true }));
      setUrlRef.current(id, result.link);
    }
  };

  // Check each link once it settles. Runs when the page opens (so a site that was shared and later
  // made private shows up) and again whenever a link changes. Editing one row does not re-ask the others.
  const signature = rows.map((r) => `${r.id}|${r.url || ''}`).join('\n');
  useEffect(() => {
    if (!enabled) return undefined;
    const timers = [];
    rowsRef.current.forEach((r) => {
      if (!protoFileKey(r.url)) {
        delete asked.current[r.id];
        setChecks((c) => (c[r.id] ? { ...c, [r.id]: undefined } : c));
        return;
      }
      if (asked.current[r.id] === r.url) return;
      timers.push(setTimeout(() => runCheck(r.id, r.url), SETTLE_MS));
    });
    // Forget notes for rows that no longer exist (deleted, or their width changed), so a new row
    // with the same id does not inherit them.
    const ids = new Set(rowsRef.current.map((r) => String(r.id)));
    const prune = (m) => {
      const keep = {};
      Object.keys(m).forEach((k) => {
        if (ids.has(k)) keep[k] = m[k];
      });
      return Object.keys(keep).length === Object.keys(m).length ? m : keep;
    };
    setTouched(prune);
    setConverted(prune);
    Object.keys(asked.current).forEach((k) => {
      if (!ids.has(k)) delete asked.current[k];
    });
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled]);

  const noteKind = (id, url) => {
    if (!enabled || !url) return null;
    const kind = classifyFigmaLink(url);
    if ((kind === 'not_figma' || kind === 'unsupported') && touched[id]) return kind;
    // a design/file link that could not be converted is not a prototype link either
    if (kind === 'design' && touched[id]) return 'unsupported';
    const check = checks[id];
    if (check && check.url === url) {
      if (check.status === 'checking') return 'checking';
      if (check.status === 'viewable') return 'viewable';
      if (check.status === 'not_viewable') return 'not_viewable';
    }
    if (converted[id] && kind === 'proto') return 'converted';
    return null;
  };

  const guide = {
    openFor: guideFor,
    open: (id) => setGuideFor(id),
    // closing the guide checks once more, so the note behind it is never out of date
    close: () => {
      const row = rowsRef.current.find((r) => r.id === guideFor);
      setGuideFor(null);
      if (row && row.url) runCheck(row.id, row.url);
    },
    // the guide asks every few seconds: do not flash the spinner behind it
    recheckQuiet: () => {
      const row = rowsRef.current.find((r) => r.id === guideFor);
      return row ? runCheck(row.id, row.url, { silent: true }) : Promise.resolve('unknown');
    },
  };

  return {
    noteKind,
    handlePaste,
    handleBlur,
    recheck: (id, url) => runCheck(id, url),
    guide,
  };
}
